// Modules
import { connect } from 'react-redux';
import React from 'react';
import PropTypes from 'prop-types';
import VelocityTransitionGroup from 'velocity-react/velocity-transition-group';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { RelativeTime } from 'components/widgets/dates';
import { RoundLoader } from 'components/widgets/state-indicators/loader';
import { SHORT_HUMAN_DATE_FORMAT, LONG_HUMAN_DATE_TIME_FORMAT } from 'constants';
import { alerts } from 'modules/alerts/actions';
import { getNextRecurrenceDate, getRecurrenceDescription } from 'utils/form-utils';
import { longDate } from 'utils/format-utils';
import { slideClosed, slideOpen, leaveSide, enterSide } from 'utils/transition-groups';
import { stopPropagation, preventDefault } from 'utils/event-handlers';
import SVG from 'components/widgets/icons/svg';
import Task from 'services/data/task';
import TaskAssign from 'components/widgets/overlays/task-assign';
import TaskDatePicker from 'components/models/ticket/task-date-picker';
import Ticket from 'services/data/ticket';
import TicketLink from 'components/models/ticket/ticket-link';
import Tooltip from 'components/widgets/overlays/tooltip';
import UrgencyPicker from 'components/widgets/overlays/urgency-picker';

import NewTask from './new-task';
import TaskChecklist from './task-checklist';
import TaskDescription from './task-description';
import TaskNotes from './task-notes';


class TaskComponent extends React.Component {
  static propTypes = {
    task: PropTypes.object.isRequired,
    updateTask: PropTypes.func,
    teams: PropTypes.array.isRequired,
    hideLowPriority: PropTypes.bool,
    currentUser: PropTypes.object.isRequired,
    ticket: PropTypes.object.isRequired,
    updateTicket: PropTypes.func.isRequired,
    inTicket: PropTypes.bool,
    query: PropTypes.object
  };

  constructor() {
    super();

    this.state = {};

    this.updateTask = this.updateTask.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.setUrgency = this.setUrgency.bind(this);
    this.assignTask = this.assignTask.bind(this);
    this.switchStatus = this.switchStatus.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const nextTask = _.get(nextProps, 'task');
    const currentTask = _.get(this.props, 'task');

    // If the state has changed re-render right away
    if (this.state !== nextState) return true;

    // Only re-render if the task has been updated
    if (nextTask && currentTask) return !moment(nextTask.updatedAt).isSameOrBefore(currentTask.updatedAt);

    return true;
  }

  // Provide storeUpdates in addition to updates if you want to alter the
  // store in a different way from the server, otherwise the server and store will
  // receive the same data
  updateTask(serverUpdates, storeUpdates) { // eslint-disable-line react/sort-comp
    const { task } = this.props;
    const updates = storeUpdates || serverUpdates;

    // Update the store
    this.props.updateTask(updates);

    if (!serverUpdates) return;

    this.setState({ saving: true, savingFailed: false });

    return Ticket.update({
      id: this.props.ticket.id,
      tasks: [{ id: task.id, ...serverUpdates }]
    })
      .tap(savedTicket => {
        if (savedTicket.openTaskCount !== this.props.ticket.openTaskCount) {
          this.props.updateTicket({ openTaskCount: savedTicket.openTaskCount });
        }
        
        this.setState({ saving: false, savingFailed: false });
      })
      .catch(() => {
        this.setState({ saving: false, savingFailed: serverUpdates });
      });
  }

  setUrgency(urgency) {
    this.updateTask({ priority: urgency });
  }

  assignTask(assignedTo) {
    const previouslyAssignedIds = _.map(this.props.task.assignedTo, 'id');
    const assignedIds = _.map(assignedTo, 'id');
    
    // In order to unassign the task, we need to send in the specific ids of users it
    // was assigned to which it is no longer assigned to with a _detach key
    const detachments = _(previouslyAssignedIds)
        .reject((id) => _.includes(assignedIds, id))
        .map((id) => ({ id, _detach: true }))
        .value();
    
    // And we send the list of users which the task is now assigned to
    const assignments = _.map(assignedIds, (id) => ({ id }));
    const serverAssignedTo = assignments.concat(detachments);

    const serverUpdate = { assignedTo: serverAssignedTo };
    const storeUpdate = { assignedTo };
    this.updateTask(serverUpdate, storeUpdate);
  }

  switchStatus() {
    const { task: propTask, ticket, currentUser } = this.props;
    const task = _.clone(propTask);
    const recurring = _.get(task, 'recurrence.enabled');

    task.status = (task.status === 'Complete') ? 'Active' : 'Complete';

    // If the task is now complete, close the task
    if (task.open && task.status === 'Complete') this.close();

    if (task.status === 'Complete' && ticket.openTaskCount <= 1 && !recurring) {
      // If the task is now complete and it was the last active task on the ticket, require resolution
      this.props.updateTask({ completedBy: currentUser.id, status: 'Complete' }, { showClosingLastTaskModal: true, lastClosedTaskId: task.id });
    } else if (task.status === 'Complete') {
      // If the task is now complete and this was not the last task to be completed, just update the store/server
      this.updateTask({ status: task.status, completedAt: moment().format(), completedBy: currentUser.id });

      if (recurring) {
        const nextDate = getNextRecurrenceDate(task.recurrence, task);
        const difference = moment().diff(nextDate, 'day');

        alerts.success({
          title: `Task will recur on ${longDate(nextDate)} (in ${Math.abs(difference)} days)`,
          subtitle: <TicketLink ticket={ticket} hideOnRouteMatch>View ticket</TicketLink>
        });
      }
    } else {
      // If the task is now active, just update the store/server
      this.updateTask({ status: task.status });
    }
  }

  open() {
    const { task: propTask, hideLowPriority, inTicket } = this.props;
    const task = _.clone(propTask);

    // Don't allow opening tasks inside of tickets, they are already open
    if (inTicket) return;

    // Don't allow opening low priority tasks while they are collapsed
    if (hideLowPriority && task.urgency === 'low') return;
    // Don't allow opening open tasks or complete tasks
    if (task.open || task.status === 'Complete') return;
    
    this.props.updateTask(_.assign(task, { open: true }));
  }

  close() {
    const task = _.clone(this.props.task);
    
    if (!task.open) return;
    
    task.open = false;
    this.props.updateTask(task);
  }

  render() {
    const { teams, ticket, currentUser, inTicket, query } = this.props;
    const { saving, savingFailed } = this.state;
    const open = (this.props.task.open || inTicket);
    const task = _.defaults({ open }, this.props.task);
    const complete = task.status === 'Complete';
    const active = task.status === 'Active';
    const future = _.get(query, 'where.dueDate.max') && moment(task.dueDate).isAfter(query.where.dueDate.max);
    const recurring = _.get(task, 'recurrence.enabled');

    const isOverdue = Task.isOverdue(task);
    const isToday = Task.isToday(task);
    const completedToday = Task.completedToday(task);

    task.urgency = Task.urgency(task);
    const hasIncompleteChecklistItems = _.some(task.checklist, { status: 'Active', parent: null });
    // Display the task as if it were closed if it's the one that triggered ticket resolution
    const taskIsLastClosed = ticket.lastClosedTaskId && ticket.lastClosedTaskId === task.id;
    const status = taskIsLastClosed ? 'Complete' : task.status;
    const hasChecklist = _.some(task.checklist, item => _.includes(['Active', 'Complete'], item.status) && !item.parent);

    const classes = classNames(
      'task',
      `status-${status.toLowerCase()}`,
      `urgency-${task.urgency}`,
      {
        open: task.open,
        'due-after-today': !isToday && !isOverdue
      }
    );

    const styles = {};

    const recurrenceLabel = (
      <span>
        {getRecurrenceDescription(task.recurrence)}
        <br />Will recur next on: {longDate(getNextRecurrenceDate(task.recurrence, task))}
      </span>
    );

    if (future) {
      const days = moment(task.dueDate).diff(query.where.dueDate.max, 'days');
      const factor = (30 - (days > 30 ? 30 : days)) / 30;

      styles.opacity = factor * 0.6 + 0.25;
    }

    if (!task.id) return <NewTask task={task} className={classes} />;

    return (
      <li onClick={preventDefault(stopPropagation(this.open))} className={classes} style={styles}>
        <VelocityTransitionGroup className="task-contents" component="div" leave={slideClosed} enter={slideOpen}>
          <div className="task-info">
            { saving &&
              <RoundLoader color="#333333" className="new-task-loader" />
            }

            { savingFailed &&
              <Tooltip onClick={() => this.updateTask(savingFailed)} alwaysShow title="This task didn't save properly, click here to retry!" className="task-save-error-wrapper">
                <SVG className="task-save-error-icon" src="svg/warning.svg" />
              </Tooltip>
            }

            { !saving && !savingFailed && hasIncompleteChecklistItems &&
              <Tooltip title="Can't complete a task with incomplete checklist items" className="has-incomplete-checklist">
                <SVG className="checklist-icon task-checkbox disabled" src="svg/checklist.svg" />
              </Tooltip>
            }

            { !saving && !savingFailed && !hasIncompleteChecklistItems && recurring &&
              <Tooltip title={recurrenceLabel} className="has-incomplete-checklist">
                <SVG onClick={stopPropagation(this.switchStatus)} className="task-checkbox" src="svg/refresh.svg" />
              </Tooltip>
            }

            { !saving && !savingFailed && !hasIncompleteChecklistItems && !recurring &&
              <SVG onClick={stopPropagation(this.switchStatus)} className="task-checkbox" src="svg/check.svg" />
            }

            <TaskDescription
              task={task}
              updateTask={this.updateTask}
              closeTask={this.close}
              currentUser={currentUser}
            />
            { !inTicket &&
              <VelocityTransitionGroup component="div" leave={leaveSide} enter={enterSide}>
                { open && <SVG onClick={stopPropagation(this.close)} className="close" src="svg/close.svg" /> }
              </VelocityTransitionGroup>
            }
          </div>

          { !open && active && (task.notes || hasChecklist) &&
            <div className="task-extra-peek">
              { hasChecklist && <TaskChecklist task={task} checklist={task.checklist} updateTask={this.updateTask} currentUser={currentUser} /> }
              { task.notes &&
                <div className="flex">
                  <SVG className="notes-icon" src="svg/notes.svg" />
                  <TaskNotes task={task} updateTask={this.updateTask} />
                </div>
              }
              <div className="task-extra-peek-cover"></div>
            </div>
          }

          { (open && (hasChecklist || active)) &&
            <TaskChecklist task={task} checklist={task.checklist} updateTask={this.updateTask} currentUser={currentUser} />
          }
          { open && <TaskNotes task={task} updateTask={this.updateTask} /> }

          { active &&
            <div className="task-meta">
              <div className="assigned-to-flex-wrapper">
                <TaskAssign task={task} assignTask={this.assignTask} teams={teams} />
              </div>
              <UrgencyPicker setUrgency={this.setUrgency}>
                <div className={classNames('urgency-pill', `urgency-${task.urgency}`)}>{task.priority}</div>
              </UrgencyPicker>
              <TaskDatePicker task={task} update={this.updateTask} />
            </div>
          }

          { complete && inTicket &&
            <div className="complete-task-meta">
              <span>Completed in </span>
              <RelativeTime title={getCreatedAndCompletedTooltip(task)} elementType="span" startDate={task.createdAt} date={task.completedAt} />
              <span> on </span>
              <Tooltip elementType="span" className="date-tooltip-wrapper" title={moment(task.completedAt).format(LONG_HUMAN_DATE_TIME_FORMAT)}>
                {completedToday ? 'today' : moment(task.completedAt).format(SHORT_HUMAN_DATE_FORMAT)}
              </Tooltip>
            </div>
          }
        </VelocityTransitionGroup>
      </li>
    );
  }
}

function getCreatedAndCompletedTooltip(task) {
  return (
    <div>
      <div>Created: {moment(task.createdAt).format(LONG_HUMAN_DATE_TIME_FORMAT)}</div>
      <div>Completed: {moment(task.completedAt).format(LONG_HUMAN_DATE_TIME_FORMAT)}</div>
    </div>
  );
}

export default connect((state) => ({
  hideLowPriority: state.tickets.hideLowPriority,
  currentUser: state.auth.currentUser,
  teams: state.pageData.teams,
  query: state.location.query
}))(TaskComponent);



// WEBPACK FOOTER //
// ./app/components/models/ticket/task.js